import { Menu, type MenuProps } from '@pique/component';
import { useNavigateHelper } from '@pique/core/hooks/use-navigate-helper';
// removed create-workspace types
import { WorkspaceSubPath } from '@pique/core/shared';
import {
  GlobalContextService,
  useLiveData,
  useServices,
  type WorkspaceMetadata,
  WorkspacesService,
} from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { UserWithWorkspaceList } from './user-with-workspace-list';
import { WorkspaceCard } from './workspace-card';

interface WorkspaceSelectorProps {
  open?: boolean;
  workspaceMetadata?: WorkspaceMetadata;
  onSelectWorkspace?: (workspaceMetadata: WorkspaceMetadata) => void;
  // onCreatedWorkspace removed
  showSettingsButton?: boolean;
  showEnableCloudButton?: boolean;
  showArrowDownIcon?: boolean;
  showSyncStatus?: boolean;
  disable?: boolean;
  menuContentOptions?: MenuProps['contentOptions'];
  className?: string;
}

export const WorkspaceSelector = ({
  workspaceMetadata: outerWorkspaceMetadata,
  onSelectWorkspace,
  showSettingsButton,
  showArrowDownIcon,
  disable,
  open: outerOpen,
  showEnableCloudButton,
  showSyncStatus,
  className,
  menuContentOptions,
}: WorkspaceSelectorProps) => {
  const { workspacesService, globalContextService } = useServices({
    GlobalContextService,
    WorkspacesService,
  });
  const [innerOpen, setOpened] = useState(false);
  const open = outerOpen ?? innerOpen;
  const closeUserWorkspaceList = useCallback(() => {
    setOpened(false);
  }, []);
  const openUserWorkspaceList = useCallback(() => {
    setOpened(true);
  }, []);

  const currentWorkspaceId = useLiveData(
    globalContextService.globalContext.workspaceId.$
  );
  const currentWorkspaceMetadata = useLiveData(
    currentWorkspaceId
      ? workspacesService.list.workspace$(currentWorkspaceId)
      : null
  );
  const workspaceMetadata = outerWorkspaceMetadata ?? currentWorkspaceMetadata;

  // revalidate workspace list when open workspace list
  useEffect(() => {
    if (open) {
      workspacesService.list.revalidate();
    }
  }, [workspacesService, open]);

  return (
    <Menu
      rootOptions={{
        open,
      }}
      items={
        <UserWithWorkspaceList
          onEventEnd={closeUserWorkspaceList}
          onClickWorkspace={onSelectWorkspace}
          showEnableCloudButton={showEnableCloudButton}
          showSettingsButton={showSettingsButton}
        />
      }
      contentOptions={{
        // hide trigger
        sideOffset: -58,
        onInteractOutside: closeUserWorkspaceList,
        onEscapeKeyDown: closeUserWorkspaceList,
        ...menuContentOptions,
        style: {
          width: '300px',
          ...menuContentOptions?.style,
        },
      }}
    >
      {workspaceMetadata ? (
        <WorkspaceCard
          workspaceMetadata={workspaceMetadata}
          onClick={openUserWorkspaceList}
          showSyncStatus={showSyncStatus}
          className={className}
          showArrowDownIcon={showArrowDownIcon}
          disable={disable}
          data-testid="current-workspace-card"
        />
      ) : (
        <span></span>
      )}
    </Menu>
  );
};

export const WorkspaceNavigator = ({
  onSelectWorkspace,
  ...props
}: WorkspaceSelectorProps) => {
  const { jumpToSubPath } = useNavigateHelper();

  const handleClickWorkspace = useCallback(
    (workspaceMetadata: WorkspaceMetadata) => {
      onSelectWorkspace?.(workspaceMetadata);
      // Workspace switching now goes through bootstrap
      // Simply navigate to the workspace, which will handle the switch
      if (document.startViewTransition) {
        try {
          const vt = document.startViewTransition(async () => {
            try {
              jumpToSubPath(workspaceMetadata.id, WorkspaceSubPath.ALL);
              await new Promise(resolve => setTimeout(resolve, 150));
            } catch (err) {
              console.error('Error in view transition callback:', err);
            }
          });
          // Handle all view transition promises to avoid unhandled rejections
          void Promise.allSettled([
            vt.updateCallbackDone,
            vt.finished,
            vt.ready
          ]).catch(() => {
            // Silently ignore all view transition errors
          });
        } catch (err) {
          // Fallback without view transition if it throws synchronously (e.g., overlapping transition)
          // Only log non-AbortError exceptions
          if (err instanceof Error && err.name !== 'AbortError') {
            console.warn('View transition error:', err);
          }
          jumpToSubPath(workspaceMetadata.id, WorkspaceSubPath.ALL);
        }
      } else {
        jumpToSubPath(workspaceMetadata.id, WorkspaceSubPath.ALL);
      }
    },
    [onSelectWorkspace, jumpToSubPath]
  );
  // created workspace flow removed
  return (
    <WorkspaceSelector
      onSelectWorkspace={handleClickWorkspace}

      {...props}
    />
  );
};
